/**
 * PostHog feature-flag keys consumed by the rules engine. Same keys as Sparta so
 * a single PostHog rollout drives both apps.
 *
 * NOTE — Split-gated features omitted here. Sparta gates several onboarding
 * features behind Split (read via `isExperimentOn`/`isExperimentOnAsync`), NOT
 * PostHog. Onboarding has no Split integration, so those features are simply
 * enabled by default (no flag read) — they are intentionally absent from this
 * map. The Split-gated features and their Sparta keys are:
 *   - Initial-consult scheduling  → `sparta_enable_onboarding_initial_consult_schedule`
 *   - Quick Intro Messages step   → `sparta_enable_quick_intro_message`
 *   - "Add To Calendar" button    → `sparta_enable_add_to_calendar`
 *   - Prescribed-program auto-join → `sparta_auto_join_program`
 */
export const FEATURE_FLAGS = {
  /**
   * Multivariate; the `'test'` variant turns on the post-enrollment testimonial
   * page (further gated by modality / program / language — see
   * `shouldShowTestimonial`). Sparta's `motivation__outcomes_before_coach_selection`.
   */
  OUTCOMES_BEFORE_COACH_SELECTION: 'motivation__outcomes_before_coach_selection',
  /**
   * Multivariate; the `'test'` variant surfaces the top-matched coach as the
   * highlighted "Best fit" recommendation (otherwise the coaches render as a flat
   * list with no hard recommend). Sparta's `coach_matching__hard_recommend_top_pick`.
   */
  COACH_HARD_RECOMMEND_TOP_PICK: 'coach_matching__hard_recommend_top_pick',
  /**
   * Multivariate; the `'test'` variant sends desktop users to the web app at
   * onboarding completion instead of the transition screen. The PostHog cohort
   * already targets desktop + non-SSO users, so the code only checks the variant.
   * Sparta's `paradigm__desktop_users_direct_to_web_app`.
   */
  DESKTOP_USERS_DIRECT_TO_WEB_APP: 'paradigm__desktop_users_direct_to_web_app',
  /**
   * Boolean; when on (paired with DESKTOP_USERS_DIRECT_TO_WEB_APP) the completion
   * redirect targets `/app` rather than home. Sparta's `app__web_app_redirect`.
   */
  WEB_APP_REDIRECT: 'app__web_app_redirect',
  /**
   * Multivariate experiment (control | test); the `test` arm serves the migrated
   * onboarding surveys via the new dynamic-assessment sessions API instead of
   * the legacy screens. The control arm runs the legacy flow. Read via
   * `getFeatureFlag(...) === 'test'` (like DESKTOP_USERS_DIRECT_TO_WEB_APP),
   * loaded as a resource by `useOnboardingResources.ensureDynamicAssessment`.
   * Migrated surveys (DYNAMIC_ONBOARDING_FLOW_KEYS): health conditions, health
   * goals, the 3 AOM screeners, US state, lab preference, the pre-diabetes
   * (diabetes_decision) screener, and phq/gad. In the test arm the resolver
   * routes the whole survey region to `/step/dynamic-assessment`, where
   * DynamicAssessmentFlow serves the flows sequentially (then the legacy flow at
   * `/step/assigned-screeners` serves any unmigrated screener — today none); the
   * standalone health-conditions / health-goals / pre-diabetes / phq-gad steps
   * are skipped.
   * Control walks the legacy screens unchanged. A dynamic-pass error abandons the
   * whole survey region and skips to the program region — all-or-nothing, no
   * legacy fallback (see dynamicAssessmentErrored).
   */
  ONB_USE_DYNAMIC_ASSESSMENT: 'onb_use_dynamic_assessment',
  /**
   * Multivariate experiment (ME-1293); the `'test'` variant inserts the Member
   * Contact form (address + phone) after the coach-scheduling phase, before the
   * terminal transition. `'control'` skips it. Read in RulesEngineContext (via
   * `useFeatureFlagVariant`) and composed into `showMemberContact`.
   */
  MEMBER_CONTACT_IN_ONBOARDING: 'member_contact__collect_in_onboarding',
} as const;

/** Programs (by `internalName`) that show the testimonial page. Mirrors Sparta. */
export const TESTIMONIAL_PROGRAM_INTERNAL_NAMES = ['Medical Weight Loss', 'Weight Loss+'];

/** Locale gate for the testimonial page (Sparta shows it for en-US only). */
export const TESTIMONIAL_LANGUAGE = 'en-US';
